// FILE: src/components/shell/navigation.js
// Side navigation per role. `exact` items are active only on their own path;
// the rest also for their sub-pages (e.g. a ticket under Support), except
// the sub-pages listed in `exclude` that have their own item. Staff items
// name the permission (`need`, see src/auth/permissions.js) their page asks
// for; items the signed-in staff member can't use are not shown.
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ComputerOutlinedIcon from "@mui/icons-material/ComputerOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import AccessibilityNewOutlinedIcon from "@mui/icons-material/AccessibilityNewOutlined";
import AddLinkIcon from "@mui/icons-material/AddLink";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";

export const STUDENT_NAV = [
  {
    heading: "Learning",
    items: [
      { label: "Dashboard", path: "/student", icon: SpaceDashboardOutlinedIcon, exact: true },
      { label: "Course information", path: "/student/course-info", icon: InfoOutlinedIcon },
      { label: "Course survey", path: "/student/survey", icon: FactCheckOutlinedIcon }
    ]
  },
  {
    heading: "Help",
    items: [
      { label: "Help centre", path: "/student/help", icon: HelpOutlineIcon },
      { label: "Glossary", path: "/student/glossary", icon: TranslateOutlinedIcon },
      { label: "Support", path: "/student/support", icon: SupportAgentOutlinedIcon },
      { label: "System requirements", path: "/student/system-requirements", icon: ComputerOutlinedIcon }
    ]
  },
  {
    heading: "Account",
    items: [
      { label: "My profile", path: "/student/profile", icon: PersonOutlineIcon },
      { label: "My activity", path: "/student/activity", icon: HistoryOutlinedIcon }
    ]
  }
];

export const ADMIN_NAV = [
  {
    heading: "Overview",
    items: [
      { label: "Dashboard", path: "/admin", icon: SpaceDashboardOutlinedIcon, exact: true, need: "dashboard.view" },
      { label: "Quiz results", path: "/admin/quiz-results", icon: QuizOutlinedIcon, need: "dashboard.view" }
    ]
  },
  {
    heading: "People",
    items: [
      { label: "Students", path: "/admin/users", icon: PeopleOutlineIcon, need: "students.view" },
      {
        label: "Support requests",
        path: "/admin/support",
        icon: SupportAgentOutlinedIcon,
        exclude: ["/admin/support/report"],
        need: "support.view"
      },
      { label: "Service levels", path: "/admin/support/report", icon: InsightsOutlinedIcon, need: "support.report" },
      { label: "Accommodations", path: "/admin/accommodations", icon: AccessibilityNewOutlinedIcon, need: "accommodations.manage" },
      { label: "Course survey", path: "/admin/feedback", icon: RateReviewOutlinedIcon, need: "survey.view" }
    ]
  },
  {
    heading: "Course & records",
    items: [
      { label: "Lessons", path: "/admin/resources", icon: MenuBookOutlinedIcon, need: "content.view" },
      { label: "Curricula", path: "/admin/curricula", icon: AccountTreeOutlinedIcon, need: "content.view" },
      { label: "Compliance reports", path: "/admin/compliance", icon: AssessmentOutlinedIcon, need: "records.view" },
      { label: "Records & exports", path: "/admin/exports", icon: FileDownloadOutlinedIcon, need: "records.export" },
      { label: "Audit log", path: "/admin/audit-logs", icon: ReceiptLongOutlinedIcon, need: "audit.view" },
      { label: "Reviewer access links", path: "/admin/access-links", icon: AddLinkIcon, need: "access_links.manage" }
    ]
  },
  {
    heading: "Staff & roles",
    items: [
      { label: "Staff", path: "/admin/staff", icon: BadgeOutlinedIcon, need: "staff.manage" },
      { label: "Roles", path: "/admin/roles", icon: AdminPanelSettingsOutlinedIcon, need: { anyOf: ["roles.manage", "staff.manage"] } }
    ]
  }
];

/** The sections with only the items `can` allows; empty sections dropped. */
export function visibleSections(sections, can) {
  return sections
    .map((section) => ({ ...section, items: section.items.filter((item) => !item.need || can(item.need)) }))
    .filter((section) => section.items.length > 0);
}

/** Where a staff member starts: the first page in the navigation they can use (null: none). */
export function staffHome(can) {
  for (const section of ADMIN_NAV) {
    const item = section.items.find((i) => !i.need || can(i.need));
    if (item) return item.path;
  }
  return null;
}

export function isActive(item, pathname) {
  if ((item.exclude || []).some((p) => pathname === p || pathname.startsWith(`${p}/`))) return false;
  if (item.exact) return pathname === item.path || pathname === `${item.path}/`;
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}
